// @flow
import React, { Component } from "react";
import classNames from "classnames";
import Input from "@material-ui/core/Input";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import { withStyles } from "@material-ui/core/styles";

import type { DictionaryType } from "../../types/GeneralTypes";
import type { SingleTextFieldClassesType } from "./SingleTextField";
import { baseStyles } from "./SingleTextField";

export const InputStyles = (theme: DictionaryType): DictionaryType => ({
  ...baseStyles(theme),
  textFieldRoot: {
    padding: 0,
    marginBottom: theme.spacing.unit * 2,
    "label + &": {
      marginTop: theme.spacing.unit * 4
    }
  },
  textFieldInput: {
    borderRadius: 4,
    background: theme.palette.common.white,
    border: "1px solid #ced4da",
    fontSize: "1rem",
    padding: "10px 12px",
    width: "calc(100% - 24px)",
    transition: "box-shadow 0.25s ease-in-out",
    "&:focus": {
      borderColor: theme.palette.primary.light,
      boxShadow: "0 0 0 0.2rem " + theme.palette.primary.third
    }
  },
  textFieldFormLabel: {
    fontSize: "1.0625rem",
    fontWeight: 500,
    padding: "10px 5px"
  },
  borderError: {
    borderColor: theme.palette.error.main,
    "&:focus": {
      borderColor: theme.palette.error.main,
      boxShadow: "0 0 0 0.1rem " + theme.palette.error.main
    }
  },
  helperText: {
    marginTop: "-5px",
    paddingLeft: "6px"
  },
  capitalize: {
    textTransform: "capitalize"
  }
});

export type StyledTextFieldClassesType = {
  ...SingleTextFieldClassesType,
  textFieldRoot: DictionaryType,
  textFieldInput: DictionaryType,
  textFieldFormLabel: DictionaryType,
  borderError: DictionaryType,
  helperText: DictionaryType,
  capitalize: DictionaryType
};

type StyledTextFieldPropsType = {
  label: string,
  name: string,
  value: string,
  inpuType: string,
  onChange: Function,
  classes: StyledTextFieldClassesType,
  className?: string,
  isRequired?: boolean,
  isInvalid?: boolean,
  isAutoFocus?: boolean,
  isDisabled?: boolean,
  onBlur?: Function,
  onFocus?: Function,
  onEnter?: Function,
  endAdornment?: React$Element<*>,
  startAdornment?: React$Element<*>,
  helper?: string,
  isCapitalize?: boolean,
  rows?: number,
  width?: string,
  inputComponent?: React$Element<*>,
  placeholder?: string
};

class StyledTextField extends Component<StyledTextFieldPropsType, {}> {
  render(): React$Element<*> {
    const classes = this.props.classes;

    return (
      <FormControl
        fullWidth={!this.props.width}
        disabled={this.props.isDisabled}
        style={this.props.width ? { width: this.props.width } : {}}
      >
        {this.props.label !== "" && (
          <InputLabel
            htmlFor={this.props.name}
            required={this.props.isRequired}
            error={this.props.isInvalid}
            shrink={true}
            classes={{
              root: classes.greenLabel,
              formControl: classes.textFieldFormLabel,
              focused: classes.labelFocused,
              error: classes.labelError,
              disabled: classes.labelDisabled
            }}
          >
            {this.props.label}
          </InputLabel>
        )}
        <Input
          id={this.props.name}
          name={this.props.name}
          value={this.props.value}
          className={this.props.className}
          classes={{
            root: classes.textFieldRoot,
            input: classNames(
              classes.textFieldInput,
              this.props.isInvalid && classes.borderError,
              this.props.isCapitalize && classes.capitalize
            )
          }}
          error={this.props.isInvalid}
          onChange={this.props.onChange}
          onBlur={this.props.onBlur}
          onFocus={this.props.onFocus}
          type={this.props.inpuType}
          fullWidth={true}
          disableUnderline={true}
          inputProps={{
            required: this.props.isRequired
          }}
          autoFocus={this.props.isAutoFocus}
          onKeyPress={(ev: SyntheticKeyboardEvent<*>) => {
            if (this.props.onEnter !== undefined) {
              if (ev.key === "Enter") {
                this.props.onEnter();
                ev.preventDefault();
              }
            }
          }}
          endAdornment={this.props.endAdornment}
          startAdornment={this.props.startAdornment}
          multiline={this.props.rows !== undefined}
          rows={this.props.rows}
          inputComponent={this.props.inputComponent}
          disabled={this.props.isDisabled}
          placeholder={this.props.placeholder}
        />
        {this.props.helper && (
          <FormHelperText
            error={this.props.isInvalid}
            classes={{ root: classes.helperText }}
          >
            {this.props.helper}
          </FormHelperText>
        )}
      </FormControl>
    );
  }
}

export default withStyles(InputStyles)(StyledTextField);
